import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * One popup at a time, in a deliberate order.
 *
 * Startup popups were each mounted independently with their own visibility
 * flag, and the only coordination anywhere was a single hardcoded prop —
 * `<DailyRewardManager blockShow={showGlobalGuidelines} />`. One pair, one
 * direction, nothing else aware of anything else. So they stacked: the security
 * PIN prompt and the guide could be on screen together, and a tap meant for the
 * front one dismissed what was behind it, losing it entirely.
 *
 * Each popup declares that it WANTS to show and how important it is. Only the
 * front of the queue actually renders; the rest keep their intent and wait, so
 * nothing is lost — it simply happens later. Dismissing the front one hands the
 * slot to whatever is next on the same tick.
 *
 * Popups keep their own state. This decides only whose turn it is, which is why
 * wiring one up is a single hook call rather than a rewrite.
 */

export const POPUP_PRIORITY = {
  /** Security setup blocks everything: it is about account safety. */
  SECURITY: 100,
  /** Legal acknowledgement gates use of the app, so it precedes anything optional. */
  TERMS_UPDATE: 90,
  /** Community guidelines, shown once after signup. */
  GUIDELINES: 80,
  /** The onboarding / what's-new tour. */
  GUIDE: 50,
  /** Rewards are the most interruptible thing here. */
  DAILY_REWARD: 20,
} as const;

interface Entry {
  id: string;
  priority: number;
  wants: boolean;
  /** Registration order, so equal priorities resolve deterministically. */
  seq: number;
}

interface PopupQueueApi {
  register: (entry: Omit<Entry, 'seq'>) => void;
  unregister: (id: string) => void;
  currentId: string | null;
}

const PopupQueueContext = createContext<PopupQueueApi | null>(null);

/**
 * Empty gap between one popup leaving and the next arriving.
 *
 * Ordering alone did not stop the original bug, it only narrowed it. The tap
 * that dismisses the front popup is still travelling when the next one mounts,
 * so the replacement appears directly under the finger and takes the tail of the
 * same interaction: releasing the PIN prompt immediately dismissed whatever the
 * queue handed the slot to. Long enough to outlast a tap's touchend and any
 * exit animation, short enough to read as one popup following another.
 */
const HANDOFF_DELAY_MS = 350;

export function PopupQueueProvider({ children }: { children: ReactNode }) {
  const entries = useRef(new Map<string, Entry>());
  const seqRef = useRef(0);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const handoffTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // recompute schedules itself after the gap, so it needs a stable handle to the
  // latest copy of itself without taking itself as a dependency.
  const recomputeRef = useRef<() => void>(() => {});

  // Recomputed from the registry rather than tracked incrementally: the front of
  // the queue is a pure function of who currently wants to show.
  const recompute = useCallback(() => {
    let best: Entry | null = null;
    for (const entry of entries.current.values()) {
      if (!entry.wants) continue;
      if (
        !best
        || entry.priority > best.priority
        || (entry.priority === best.priority && entry.seq < best.seq)
      ) {
        best = entry;
      }
    }
    const next = best ? best.id : null;

    setCurrentId(prev => {
      // Guarded so an unchanged winner does not re-render every consumer.
      if (prev === next) return prev;

      // Only a HANDOFF is delayed — one popup replacing another. Opening the
      // first popup of a session, and closing the last one, both happen at once:
      // there is no stray tap to protect against in either direction, and
      // delaying them would just make the app feel unresponsive.
      if (prev !== null && next !== null) {
        if (handoffTimer.current) clearTimeout(handoffTimer.current);
        handoffTimer.current = setTimeout(() => {
          handoffTimer.current = null;
          recomputeRef.current();
        }, HANDOFF_DELAY_MS);
        return null; // screen is clear during the gap
      }

      return next;
    });
  }, []);

  recomputeRef.current = recompute;

  // A pending handoff must not outlive the provider.
  useEffect(() => () => {
    if (handoffTimer.current) clearTimeout(handoffTimer.current);
  }, []);

  const register = useCallback((entry: Omit<Entry, 'seq'>) => {
    const existing = entries.current.get(entry.id);
    // Same intent as last time: nothing to do. Without this the effect that
    // calls register on every render would recompute endlessly.
    if (existing && existing.wants === entry.wants && existing.priority === entry.priority) {
      return;
    }
    entries.current.set(entry.id, {
      ...entry,
      seq: existing ? existing.seq : seqRef.current++,
    });
    recompute();
  }, [recompute]);

  const unregister = useCallback((id: string) => {
    if (!entries.current.delete(id)) return;
    recompute();
  }, [recompute]);

  const value = useMemo(
    () => ({ register, unregister, currentId }),
    [register, unregister, currentId],
  );

  return (
    <PopupQueueContext.Provider value={value}>
      {children}
    </PopupQueueContext.Provider>
  );
}

/**
 * Claim a place in the queue.
 *
 * @param id       stable and unique per popup.
 * @param priority from POPUP_PRIORITY.
 * @param wants    whether this popup would show if nothing else were in the way.
 * @returns whether it is this popup's turn RIGHT NOW. Render on true, and
 *          nothing else — returning false is "wait", never "give up".
 *
 * Outside a provider this returns `wants` unchanged, so a popup used somewhere
 * the provider is not mounted behaves exactly as it did before.
 */
export function usePopupSlot(id: string, priority: number, wants: boolean): boolean {
  const ctx = useContext(PopupQueueContext);

  // Destructured, and the effects below depend on these FUNCTIONS rather than on
  // the context object.
  //
  // That distinction was causing the popups to flash. The context value is a
  // useMemo over [register, unregister, currentId], so its identity changes
  // every time the slot changes hands. Both effects below listed `ctx` as a
  // dependency, so every slot change re-ran them — and the second one's cleanup
  // is ctx.unregister(id). So each transition went:
  //
  //   currentId changes -> ctx is a new object -> cleanup fires ->
  //   unregister -> recompute -> currentId changes -> ctx is a new object -> ...
  //
  // with the first effect re-registering in between. The queue oscillated
  // between null and the winner instead of settling, and any popup rendering on
  // "do I hold the slot" mounted and unmounted on every cycle.
  //
  // register and unregister are useCallback-stable for the life of the provider
  // (recompute has no dependencies), so keying the effects on them means a
  // registration is dropped only when this hook really unmounts or its id
  // changes — never merely because somebody else took the slot.
  const register = ctx?.register;
  const unregister = ctx?.unregister;

  useEffect(() => {
    if (!register) return;
    register({ id, priority, wants });
  }, [register, id, priority, wants]);

  // Deliberately separate from the effect above: that one re-runs whenever
  // `wants` changes and must not drop the registration each time.
  useEffect(() => {
    if (!unregister) return;
    return () => unregister(id);
  }, [unregister, id]);

  if (!ctx) return wants;
  return wants && ctx.currentId === id;
}

/** True when any popup currently holds the slot. */
export function useAnyPopupOpen(): boolean {
  const ctx = useContext(PopupQueueContext);
  return !!ctx?.currentId;
}
